import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import { Box, Typography, TextField, Divider  } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { DatePicker } from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';
import SingleSelect from 'components/common/SingleSelect';
import { useNotificationContext } from 'contexts/NotificationContext';
import salesforceFundraisingsService from 'services/salesforceFundraisingsService';
import Drawer from 'features/IRWorkstation/components/Common/Drawer/Drawer';

// Track Events
import { useTrackEvent } from 'hooks/useTrackEvent';
import { ButtonClickTargets } from 'constants/buttonClickTargets';
import { PageLoadsTargets } from 'constants/pageLoadTargets';

const FundraisingField = ({ label, children, value }) => (
	<Box sx={{ mb: 2 }}>
		<Typography
			sx={{
				fontSize: '11px',
				fontWeight: 600,
				textTransform: 'uppercase',
				color: 'text.tertiary',
				mb: '4px',
			}}
		>
			{label}
		</Typography>

		{children ?? (
			<Typography
				sx={{
					fontSize: '13px',
					fontWeight: 500,
					color: 'text.primary',
				}}
			>
				{value || '—'}
			</Typography>
		)}
	</Box>
);

FundraisingField.propTypes = {
	label: PropTypes.string.isRequired,
	children: PropTypes.node,
	value: PropTypes.node,
};

const FundraisingDrawer = ({ open, fundraising, tenant, onClose, onSaved }) => {
	const { trackButtonClick, trackPageLoad } = useTrackEvent({ tenant, product: 'IEM' });
	const { showNotification } = useNotificationContext();

	const [saving, setSaving] = useState(false);

	// Editable field state
	const [commitment, setCommitment] = useState('');
	const [stage, setStage] = useState('');
	const [anticipatedCloseDate, setAnticipatedCloseDate] = useState(null);
	const [closingRound, setClosingRound] = useState('');
	const [nextSteps, setNextSteps] = useState('');
	const [notes, setNotes] = useState('');
	const nextStepsTooLong = nextSteps.length > 1000;
	const notesTooLong = notes.length > 5000;

	// Picklist options
	const [picklistValues, setPicklistValues] = useState(null);
	const [picklistLoading, setPicklistLoading] = useState(false);
	const picklistFetched = useRef(false);

	// Initialize form state from fundraising data
	const initializeFormState = (data) => {
		if (!data) return;
		setCommitment(data.commitment ?? '');
		setStage(data.stage ?? '');
		setAnticipatedCloseDate(
			data.anticipated_close_date ? dayjs(data.anticipated_close_date) : null
		);
		setClosingRound(data.closing_round ?? '');
		setNextSteps(data.next_steps ?? '');
		setNotes(data.notes ?? '');
	};

	useEffect(() => {
		initializeFormState(fundraising);
	}, [fundraising]);

	// Fetch picklist values once when opening the drawer
	useEffect(() => {
		if (!open || picklistFetched.current) return;

		const fetchPicklists = async () => {
			setPicklistLoading(true);
			try {
				const data = await salesforceFundraisingsService.getPicklistValues();
				setPicklistValues(data);
				picklistFetched.current = true;
			} catch (err) {
				console.error('Failed to load picklist values:', err);
			} finally {
				setPicklistLoading(false);
			}
		};

		fetchPicklists();
	}, [open]);

	useEffect(() => {
		trackPageLoad(PageLoadsTargets.FundraisingDrawer);
	}, []);

	// Build a payload of only changed fields
	const buildPayload = () => {
		if (!fundraising) return {};

		const payload = {};

		const commitmentNum = commitment !== '' ? Number(commitment) : null;
		const originalCommitment = fundraising.commitment ?? null;
		if (commitmentNum !== originalCommitment) {
			payload.commitment = commitmentNum;
		}

		if (stage !== (fundraising.stage ?? '')) {
			payload.stage = stage;
		}

		const formattedDate = anticipatedCloseDate?.isValid()
			? anticipatedCloseDate.format('YYYY-MM-DD')
			: null;
		const originalDate = fundraising.anticipated_close_date ?? null;
		if (formattedDate !== originalDate) {
			payload.anticipated_close_date = formattedDate;
		}

		if (closingRound !== (fundraising.closing_round ?? '')) {
			payload.closing_round = closingRound;
		}

		if (nextSteps !== (fundraising.next_steps ?? '')) {
			payload.next_steps = nextSteps;
		}

		if (notes !== (fundraising.notes ?? '')) {
			payload.notes = notes;
		}

		return payload;
	};

	const handleSave = async () => {
		if (!fundraising || nextStepsTooLong || notesTooLong) return;
		handleButtonClickTrackEvent('save');

		const payload = buildPayload();
		if (Object.keys(payload).length === 0) {
			showNotification('No changes to save', 'info');
			return;
		}

		setSaving(true);
		try {
			await salesforceFundraisingsService.updateFundraising(
				fundraising.id,
				payload
			);

			await onSaved?.();
			showNotification('Fundraising opportunity updated', 'success');
			onClose();
		} catch (err) {
			showNotification(
				err?.message || 'Failed to update fundraising opportunity',
				'error'
			);
		} finally {
			setSaving(false);
		}
	};

	const handleCancel = () => {
		if (saving) return;
		handleButtonClickTrackEvent('cancel');
		initializeFormState(fundraising);
		onClose();
	};

	const handleButtonClickTrackEvent = (targetKey) => {
		const targetMap = {
			cancel: ButtonClickTargets.FundraisingDrawerCancel,
			save: ButtonClickTargets.FundraisingDrawerSave,
		};

		const target = targetMap[targetKey];
		if (target) {
			trackButtonClick(target);
		}
	};

	const defaultActionButtons = [
		{
			key: 'fundraising-cancel-action-button',
			label: 'Cancel',
			onClick: handleCancel,
			disabled: saving,
			variant: 'secondary',
		},
		{
			key: 'fundraising-save-action-button',
			label: 'Save Changes',
			onClick: handleSave,
			loading: saving,
			disabled: nextStepsTooLong || notesTooLong,
			variant: 'primary',
		},
	];

	return (
		<Drawer
			open={open}
			onClose={handleCancel}
			title={'Edit Fundraising Opportunity'}
			subtitle={fundraising?.name ? fundraising.name : ''}
			actionButtons={fundraising ? defaultActionButtons : []}
		>
			{/* main content rendered as children */}
			{fundraising && (
				<Box
					sx={{
						mx: 5,
						mt: 3,
						mb: 0,
						px: 2,
						py: 1.5,
						borderRadius: 1,
						backgroundColor: 'status.blue.bg',
						border: '1px solid',
						borderColor: 'status.blue.border',
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<InfoIcon fontSize='small' sx={{ color: 'status.blue.icon' }} />
						<Typography sx={{ fontSize: '12px', color: 'status.blue.text' }}>
							Changes made within this modal will flow back into the Altvia CRM.
						</Typography>
					</Box>
				</Box>
			)}

			{fundraising && (
				<Box sx={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
					{/* Read-only fields */}
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: '1fr 1fr',
							gap: '16px',
							mb: 3,
						}}
					>
						<FundraisingField
							label='Investor Type'
							value={fundraising.investor_type}
						/>
						<FundraisingField
							label='Existing Investor'
							value={fundraising.existing_investor ? 'Yes' : 'No'}
						/>
					</Box>

					<Divider sx={{ mb: 3 }} />

					{/* Editable fields */}
					<FundraisingField label='Commitment'>
						<TextField
							type='number'
							size='small'
							fullWidth
							value={commitment}
							onChange={(e) => setCommitment(e.target.value)}
							disabled={saving}
							placeholder='Enter commitment amount'
						/>
					</FundraisingField>

					<FundraisingField label='Stage'>
						<SingleSelect
							value={stage}
							options={picklistValues?.stage ?? []}
							loading={picklistLoading}
							disabled={saving}
							placeholder='Select a stage'
							onChange={(val) => setStage(val)}
						/>
					</FundraisingField>

					<FundraisingField label='Anticipated Close Date'>
						<DatePicker
							value={anticipatedCloseDate}
							onChange={(val) => setAnticipatedCloseDate(val)}
							disabled={saving}
							slotProps={{
								textField: {
									size: 'small',
									fullWidth: true,
								},
							}}
						/>
					</FundraisingField>

					<FundraisingField label='Closing Round'>
						<SingleSelect
							value={closingRound}
							options={picklistValues?.closing_round ?? []}
							loading={picklistLoading}
							disabled={saving}
							placeholder='Select a closing round'
							onChange={(val) => setClosingRound(val)}
						/>
					</FundraisingField>

					<FundraisingField label='Next Steps'>
						<TextField
							size='small'
							fullWidth
							multiline
							rows={4}
							value={nextSteps}
							onChange={(e) => setNextSteps(e.target.value)}
							disabled={saving}
							error={nextStepsTooLong}
							helperText={nextStepsTooLong
								? 'Next Steps cannot exceed 1,000 characters'
								: `${nextSteps.length.toLocaleString()} / 1,000`}
							slotProps={{ htmlInput: { 'aria-label': 'Next Steps', maxLength: 1001 } }}
							sx={{ '& textarea': { overflowY: 'auto' } }}
						/>
					</FundraisingField>

					<FundraisingField label='Notes'>
						<TextField
							size='small'
							fullWidth
							multiline
							rows={4}
							value={notes}
							onChange={(e) => setNotes(e.target.value)}
							disabled={saving}
							error={notesTooLong}
							helperText={notesTooLong
								? 'Notes cannot exceed 5,000 characters'
								: `${notes.length.toLocaleString()} / 5,000`}
							slotProps={{ htmlInput: { 'aria-label': 'Notes', maxLength: 5001 } }}
							sx={{ '& textarea': { overflowY: 'auto' } }}
						/>
					</FundraisingField>
				</Box>
			)}
		</Drawer>
	);
};

FundraisingDrawer.propTypes = {
	open: PropTypes.bool.isRequired,
	fundraising: PropTypes.object,
	tenant: PropTypes.object.isRequired,
	onClose: PropTypes.func.isRequired,
	onSaved: PropTypes.func
};

export default FundraisingDrawer;
